import React, { createContext, useState, useContext, useEffect } from 'react'
import axios from 'axios'

import { useAuth } from './AuthContext'
import config from '../config/config'
import { setFlashMessage } from './WebsiteContext'
import { useTranslation } from 'react-i18next'

const CartContext = createContext()

export const CartProvider = ({ children }) => {
  const { t } = useTranslation()
  const [cartItems, setCartItems] = useState([])
  const [products, setProducts] = useState([])
  const [cartSummary, setCartSummary] = useState(null)
  const { token } = useAuth()

  const shippingCosts = { 0: 0, 500: 200, 2000: 500, 10000: 1000 }
  const shippingThreshold = 10000

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}product`)
        setProducts(response.data)
      } catch (error) {
        console.error('Error fetching products:', error)
      }
    }
    fetchProducts()
  }, [])

  useEffect(() => {
    fetchCart()
  }, [token])

  useEffect(() => {
    fetchCartSummary()
  }, [cartItems])

  const fetchCart = async () => {
    const localCart = JSON.parse(localStorage.getItem('cart')) || []
    setCartItems(localCart)
  }

  const fetchCartSummary = () => {
    const summary = calculateCartSummary(
      cartItems,
      shippingCosts,
      shippingThreshold,
    )
    setCartSummary(summary)
  }

  const calculateCartSummary = (
    cartItems,
    shippingCosts,
    shippingThreshold,
  ) => {
    let itemsPrice = 0
    let totalWeight = 0

    cartItems.forEach((item) => {
      if (item.productVariant) {
        itemsPrice += item.productVariant.price * item.quantity
        totalWeight += item.productVariant.weight * item.quantity
      }
    })

    let shippingCost = 0
    if (itemsPrice < shippingThreshold) {
      for (const weightLimit in shippingCosts) {
        if (totalWeight <= parseInt(weightLimit)) {
          shippingCost = shippingCosts[weightLimit]
          break
        }
      }
    }

    return { itemsPrice, shippingCost, totalPrice: itemsPrice + shippingCost }
  }

  const addProductToCart = (product) => {
    setCartItems((prevItems) => {
      const updatedItems = [
        ...prevItems,
        {
          uuid: crypto.randomUUID(),
          product,
          quantity: 1,
          productVariant: null,
        },
      ]
      localStorage.setItem('cart', JSON.stringify(updatedItems))
      return updatedItems
    })
    setFlashMessage(t('PRODUCT_ADDED_TO_CART_MESSAGE'), 'success')
  }

  const addStandardPhotoToCart = (photoId, quality) => {
    const product = products.find((p) => p.name === 'STANDARD_PHOTO')
    if (!product) return

    const newItem = {
      uuid: crypto.randomUUID(),
      product,
      quantity: 1,
      productVariant: null,
      photoId,
      quality,
    }

    setCartItems((prevItems) => {
      const updatedItems = [...prevItems, newItem]
      localStorage.setItem('cart', JSON.stringify(updatedItems))
      return updatedItems
    })
    setFlashMessage(t('PHOTO_ADDED_TO_CART_MESSAGE'), 'success')
  }

  const updateItemInCart = (updatedItem) => {
    setCartItems((prevItems) => {
      const updatedItems = prevItems.map((item) =>
        item.uuid === updatedItem.uuid ? { ...item, ...updatedItem } : item,
      )
      localStorage.setItem('cart', JSON.stringify(updatedItems))
      return updatedItems
    })
  }

  const removeItemFromCart = (itemUuid) => {
    setCartItems((prevItems) => {
      const updatedItems = prevItems.filter((item) => item.uuid !== itemUuid)
      localStorage.setItem('cart', JSON.stringify(updatedItems))
      return updatedItems
    })
  }

  return (
    <CartContext.Provider
      value={{
        cartItems,
        products,
        cartSummary,
        addProductToCart,
        addStandardPhotoToCart,
        updateItemInCart,
        removeItemFromCart,
        fetchCart,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => useContext(CartContext)
